import React from 'react';
import Icon from './Icon';
import useAnchoredPanel from '../hooks/useAnchoredPanel';
import s from './Menu.module.css';

// A dropdown and the pieces that go in it.
//
// The open/closed state stays with the caller: a toolbar usually needs to know
// which of its menus is open so it can close the others, and a menu that owns
// its own state cannot answer that.
//
// `align` is which edge the panel lines up with. Right-aligned by default,
// because these hang off controls on the right of a toolbar and a left-aligned
// panel there runs off the screen.

export function Menu({ open, onClose, align = 'right', children, trigger, panelClassName }) {
  // Positioned against the trigger with `fixed` rather than absolutely inside
  // it, so the panel escapes any ancestor with `overflow`, such as a
  // scrollable table.
  const { triggerRef, panelRef, style } = useAnchoredPanel({ open, align });

  return (
    <span className={s.anchor} ref={triggerRef}>
      {trigger}
      {open ? (
        <>
          <div className={s.scrim} onClick={onClose} />
          <div
            ref={panelRef}
            className={[s.panel, panelClassName].filter(Boolean).join(' ')}
            // Hidden for the first frame, before it has been measured, so it is
            // never seen at the top-left corner on its way to the right place.
            style={style || { position: 'fixed', visibility: 'hidden' }}
          >
            {children}
          </div>
        </>
      ) : null}
    </span>
  );
}

// An item is a button with an optional glyph. Icons are passed by name rather
// than as elements so a menu reads as a list of choices instead of a list of
// markup.
export function MenuItem({ icon, children, accent = false, ...rest }) {
  return (
    <button className={[s.item, accent ? s.itemAccent : ''].filter(Boolean).join(' ')} {...rest}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <div className={s.separator} />;
}

// A panel holding a form rather than a list — the filter popovers use this.
export function MenuForm({ open, onClose, trigger, children, align = 'right' }) {
  return (
    <Menu open={open} onClose={onClose} align={align} trigger={trigger} panelClassName={s.form}>
      {children}
    </Menu>
  );
}
